import React, { useMemo, useRef } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js';
import 'chart.js/auto';
import zoomPlugin from 'chartjs-plugin-zoom';
import { useSelectedChannels } from '../../contexts/SelectedChannelsContext';
import styles from '../PlotComponent/PlotComponent.module.css';

ChartJS.register(zoomPlugin);

const MIN_SAMPLE_COUNT = 8;

function colorFromLabel(label) {
  let hash = 5383;
  for (let i = 0; i < label.length; i++) hash = ((hash << 5) + hash) + label.charCodeAt(i);
  let t = (hash >>> 0) + 0x6D2B79F5;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const rnd = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  const h = Math.floor(rnd * 360), s = 78, l = 68;
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function getChannelLabel(channel) {
  if (!channel) return null;
  if (channel.fullname) return channel.fullname;
  if (channel.source && channel.name) return `${channel.source}.${channel.name}`;
  if (channel.name) return channel.name;
  return String(channel.id);
}

function getChannelDisplayLabel(label, mappings) {
  for (const mapping of mappings ?? []) {
    const { pattern, displayName } = mapping ?? {};
    if (!(pattern instanceof RegExp)) continue;

    pattern.lastIndex = 0;
    if (!pattern.test(label)) continue;
    pattern.lastIndex = 0;

    return typeof displayName === 'function'
      ? displayName(label)
      : (displayName ?? label);
  }

  return label;
}

function getValueTypeKey(channel) {
  return channel?.value_type_id != null ? String(channel.value_type_id) : '__unknown__';
}

function getValueTypeName(channel) {
  return channel?.value_type_name || 'Value';
}

function getValueTypeUnit(channel) {
  return channel?.value_type_unit || '';
}

function getPsdYAxisLabel(channel) {
  const name = getValueTypeName(channel);
  const unit = getValueTypeUnit(channel);
  return unit ? `${name} PSD [${unit}^2/Hz]` : `${name} PSD`;
}

function getSeriesValue(series, index) {
  if (Array.isArray(series?.value)) {
    const value = Number(series.value[index]);
    return Number.isFinite(value) ? value : null;
  }

  if (Array.isArray(series?.min_value) && Array.isArray(series?.max_value)) {
    const min = Number(series.min_value[index]);
    const max = Number(series.max_value[index]);
    if (Number.isFinite(min) && Number.isFinite(max)) {
      return (min + max) / 2;
    }
  }

  return null;
}

function getValidSamples(series) {
  const timestamps = Array.isArray(series?.timestamp) ? series.timestamp : [];
  const samples = [];

  for (let i = 0; i < timestamps.length; i++) {
    const timestamp = Number(timestamps[i]);
    const value = getSeriesValue(series, i);
    if (Number.isFinite(timestamp) && value != null) {
      samples.push({ timestamp, value });
    }
  }

  return samples.sort((a, b) => a.timestamp - b.timestamp);
}

function getMedianSampleSpacingSec(samples) {
  const deltas = [];

  for (let i = 1; i < samples.length; i++) {
    const delta = samples[i].timestamp - samples[i - 1].timestamp;
    if (Number.isFinite(delta) && delta > 0) {
      deltas.push(delta);
    }
  }

  if (deltas.length === 0) return null;

  deltas.sort((a, b) => a - b);
  return deltas[Math.floor(deltas.length / 2)];
}

function resampleToUniformValues(samples, sampleSpacingSec) {
  const start = samples[0].timestamp;
  const end = samples.at(-1).timestamp;
  const sampleCount = Math.floor((end - start) / sampleSpacingSec) + 1;

  if (sampleCount < MIN_SAMPLE_COUNT) {
    return [];
  }

  const values = [];
  let sourceIndex = 0;

  for (let i = 0; i < sampleCount; i++) {
    const timestamp = start + i * sampleSpacingSec;

    while (
      sourceIndex < samples.length - 2 &&
      samples[sourceIndex + 1].timestamp < timestamp
    ) {
      sourceIndex++;
    }

    const before = samples[sourceIndex];
    const after = samples[sourceIndex + 1];

    if (!after || timestamp <= before.timestamp) {
      values.push(before.value);
      continue;
    }

    const span = after.timestamp - before.timestamp;
    if (span <= 0) {
      values.push(before.value);
      continue;
    }

    const ratio = (timestamp - before.timestamp) / span;
    values.push(before.value + ratio * (after.value - before.value));
  }

  return values;
}

function nextPowerOfTwoAtLeast(value) {
  let power = 1;
  while (power < value) power *= 2;
  return power;
}

function fft(realInput) {
  const n = realInput.length;
  const real = realInput.slice();
  const imag = new Array(n).fill(0);

  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;

    if (i < j) {
      [real[i], real[j]] = [real[j], real[i]];
      [imag[i], imag[j]] = [imag[j], imag[i]];
    }
  }

  for (let len = 2; len <= n; len <<= 1) {
    const angle = -2 * Math.PI / len;
    const wLenReal = Math.cos(angle);
    const wLenImag = Math.sin(angle);

    for (let i = 0; i < n; i += len) {
      let wReal = 1;
      let wImag = 0;

      for (let j = 0; j < len / 2; j++) {
        const evenIndex = i + j;
        const oddIndex = evenIndex + len / 2;
        const oddReal = real[oddIndex] * wReal - imag[oddIndex] * wImag;
        const oddImag = real[oddIndex] * wImag + imag[oddIndex] * wReal;

        real[oddIndex] = real[evenIndex] - oddReal;
        imag[oddIndex] = imag[evenIndex] - oddImag;
        real[evenIndex] += oddReal;
        imag[evenIndex] += oddImag;

        const nextWReal = wReal * wLenReal - wImag * wLenImag;
        wImag = wReal * wLenImag + wImag * wLenReal;
        wReal = nextWReal;
      }
    }
  }

  return { real, imag };
}

function calculatePsd(series) {
  const samples = getValidSamples(series);
  if (samples.length < MIN_SAMPLE_COUNT) {
    return null;
  }

  const sampleSpacingSec = getMedianSampleSpacingSec(samples);

  if (!sampleSpacingSec) {
    return null;
  }

  const values = resampleToUniformValues(samples, sampleSpacingSec);
  const sampleCount = values.length;

  if (sampleCount < MIN_SAMPLE_COUNT) {
    return null;
  }

  const sampleRate = 1 / sampleSpacingSec;
  const fftSize = nextPowerOfTwoAtLeast(sampleCount);
  const mean = values.reduce((sum, value) => sum + value, 0) / sampleCount;
  let windowPower = 0;
  const input = new Array(fftSize).fill(0);

  values.forEach((value, index) => {
    const windowValue = 0.5 * (1 - Math.cos((2 * Math.PI * index) / (sampleCount - 1)));
    windowPower += windowValue * windowValue;
    input[index] = (value - mean) * windowValue;
  });

  const spectrum = fft(input);
  const nyquistIndex = Math.floor(fftSize / 2);
  const data = [];

  for (let bin = 0; bin <= nyquistIndex; bin++) {
    const frequency = bin * sampleRate / fftSize;
    const magnitudeSquared =
      spectrum.real[bin] * spectrum.real[bin] +
      spectrum.imag[bin] * spectrum.imag[bin];
    const oneSidedFactor = bin === 0 || bin === nyquistIndex ? 1 : 2;
    const psd = oneSidedFactor * magnitudeSquared / (sampleRate * windowPower);

    if (Number.isFinite(frequency) && Number.isFinite(psd) && psd > 0) {
      data.push({ x: frequency, y: psd });
    }
  }

  return {
    data,
    fftSize,
    sampleRate,
  };
}

function getFrequencyBounds(datasets) {
  let maxFrequency = 0;

  for (const dataset of datasets) {
    for (const point of dataset.data ?? []) {
      const frequency = Number(point.x);
      if (Number.isFinite(frequency)) {
        maxFrequency = Math.max(maxFrequency, frequency);
      }
    }
  }

  return {
    min: 0,
    max: maxFrequency,
    minRange: maxFrequency > 0 ? maxFrequency / 1000 : undefined,
  };
}

function getPsdChartOptions(yAxisLabel, frequencyBounds) {
  return {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      x: {
        type: 'linear',
        min: frequencyBounds.min,
        max: frequencyBounds.max,
        ticks: {
          color: '#ddd',
          maxTicksLimit: 10,
          callback: (value) => Number(value).toPrecision(4),
        },
        grid: { color: '#555' },
        title: {
          display: true,
          text: 'Frequency [Hz]',
          color: '#ddd',
          font: { size: 14 },
        },
      },
      y: {
        type: 'logarithmic',
        ticks: {
          color: '#ddd',
          maxTicksLimit: 8,
          callback: (value) => Number(value).toExponential(1),
        },
        grid: { color: '#555' },
        title: {
          display: true,
          text: yAxisLabel,
          color: '#ddd',
          font: { size: 14 },
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#ddd',
          font: { size: 14 },
        },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const value = Number(tooltipItems[0].parsed.x);
            return `${Number.isFinite(value) ? value.toPrecision(6) : tooltipItems[0].label} Hz`;
          },
          label: (context) => {
            const value = Number(context.parsed.y);
            const formatted = Number.isFinite(value) ? value.toExponential(4) : context.formattedValue;
            return `${context.dataset.label}: ${formatted}`;
          },
        },
      },
      zoom: {
        limits: {
          x: {
            min: frequencyBounds.min,
            max: frequencyBounds.max,
            minRange: frequencyBounds.minRange,
          },
        },
        pan: {
          enabled: true,
          mode: 'x',
        },
        zoom: {
          wheel: {
            enabled: false,
          },
          drag: {
            enabled: true,
            modifierKey: 'ctrl',
            borderColor: 'rgba(255,255,255,0.3)',
            borderWidth: 1,
            backgroundColor: 'rgba(255,255,255,0.1)',
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
        },
      },
    },
  };
}

function createPsdPanel(channel, key) {
  return {
    key: `psd-${key}`,
    valueTypeKey: key,
    title: `${getValueTypeName(channel)} PSD`,
    yAxisLabel: getPsdYAxisLabel(channel),
    datasets: [],
    fftSizes: [],
    sampleRates: [],
  };
}

function formatSampleRateRange(sampleRates) {
  const min = Math.min(...sampleRates);
  const max = Math.max(...sampleRates);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return 'unknown';
  }

  if (Math.abs(max - min) < 1e-6) {
    return `${min.toPrecision(4)} Hz`;
  }

  return `${min.toPrecision(4)}-${max.toPrecision(4)} Hz`;
}

function formatIntegerRange(values) {
  const min = Math.min(...values);
  const max = Math.max(...values);

  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return 'unknown';
  }

  if (min === max) {
    return String(min);
  }

  return `${min}-${max}`;
}

function PsdPanel({ panel }) {
  const chartRef = useRef(null);
  const fftSizeText = formatIntegerRange(panel.fftSizes);
  const sampleRateText = formatSampleRateRange(panel.sampleRates);
  const frequencyBounds = getFrequencyBounds(panel.datasets);

  return (
    <section className={styles.plotPanel ?? ''}>
      <div className={styles.panelHeader ?? ''}>
        <div className={styles.panelTitle ?? ''}>{panel.title}</div>
        <div className={styles.panelHeaderActions ?? ''}>
          <div className={styles.panelMeta ?? ''}>
            {panel.datasets.length} channel{panel.datasets.length !== 1 ? 's' : ''}, FFT {fftSizeText}, fs {sampleRateText}
          </div>
          <button
            type="button"
            className={styles.panelButton ?? ''}
            onClick={() => chartRef.current?.resetZoom?.()}
          >
            Reset zoom
          </button>
        </div>
      </div>

      <div className={styles.panelChart ?? ''}>
        <Line
          ref={chartRef}
          data={{ datasets: panel.datasets }}
          options={getPsdChartOptions(panel.yAxisLabel, frequencyBounds)}
        />
      </div>
    </section>
  );
}

export default function PsdComponent({ data, valueTypeKey, channelLabelMappings = [] }) {
  const { selectedChannels } = useSelectedChannels();

  const selectedChannelsByLabel = useMemo(() => {
    const map = new Map();
    for (const channel of selectedChannels ?? []) {
      const label = getChannelLabel(channel);
      if (label) map.set(label, channel);
    }
    return map;
  }, [selectedChannels]);

  const psdPanels = useMemo(() => {
    if (!data) return [];

    const panels = new Map();

    const ensurePanel = (channel, fallbackKey = '__unknown__') => {
      const key = channel ? getValueTypeKey(channel) : fallbackKey;
      if (!panels.has(key)) {
        panels.set(key, createPsdPanel(channel, key));
      }
      return panels.get(key);
    };

    for (const channel of selectedChannels ?? []) {
      ensurePanel(channel, getValueTypeKey(channel));
    }

    for (const [label, series] of Object.entries(data)) {
      const channel = selectedChannelsByLabel.get(label);
      const panel = ensurePanel(channel);
      const psd = calculatePsd(series);

      if (!psd?.data?.length) continue;

      panel.datasets.push({
        label: getChannelDisplayLabel(label, channelLabelMappings),
        data: psd.data,
        borderColor: colorFromLabel(label),
        borderWidth: 1,
        fill: false,
        showLine: true,
        pointRadius: 0,
        tension: 0,
      });
      panel.fftSizes.push(psd.fftSize);
      panel.sampleRates.push(psd.sampleRate);
    }

    return Array.from(panels.values())
      .filter(panel => panel.datasets.length > 0)
      .filter(panel => !valueTypeKey || panel.valueTypeKey === valueTypeKey);
  }, [data, selectedChannels, selectedChannelsByLabel, valueTypeKey, channelLabelMappings]);

  if (!data || psdPanels.length === 0) {
    return null;
  }

  return (
    <>
      {psdPanels.map(panel => (
        <PsdPanel key={panel.key} panel={panel} />
      ))}
    </>
  );
}
